<template>
<div class="card-modal-products-list-item relative" :class="{'cart-preview-content-loading opacity-60': shouldDisplayPreloader}">
  <div v-if="shouldDisplayPreloader" class="cart-preview-preloader absolute">
    <i class="fad fa-spinner-third fa-spin text-3xl"></i>
  </div>
  <div class="cart-product-image-wrapper">
    <figure class="product-image">
      <router-link :to="productRouteParams">
        <responsive-image v-if="hasMedia" :media="variant.product.media[0]" :sizes="['large', 'medium', 'thumb']" :alt="variant.product.name" />
        <img v-else :src="photoPlaceholder" :alt="variant.product.name">
      </router-link>
    </figure>
  </div>
  <div class="cart-product-info">
    <router-link :to="productRouteParams">
      <span class="cart-product-title">{{ variant.product.name }}</span>
      <span v-if="variantDescription" class="product-variant-description">
        {{ variantDescription }}
      </span>
      <span v-if="hasModifiers" class="product-variant-modifiers-list">
        <template v-for="modifier in variantModifiersList">
          <template v-if="modifier.hasGroup">{{ modifier.group }}: </template>{{ modifier.modifier }}
          <template v-if="modifier.quantity > 1">({{ modifier.quantity }})</template>
          <span class="text-primary-darker font-bold" v-if="modifier.total_price !== null && modifier.total_price.value > 0"> +{{ modifier.total_price.full }}</span>
          <br>
        </template>
      </span>
      <span class="cart-product-price">
        <strong v-if="variant.promotion_reward_id || variant.is_promocode_reward" class="text-primary-darker text-sm">
          <i class="fal fa-gift"></i>
          Подарок
        </strong>
        <template v-else>
          <span v-if="variant.has_discount" class="line-through inline-block mr-1">
            {{ priceToText(variant.original_unit_price) }}
          </span>
          {{ priceToText(variant.unit_price) }}
          <template v-if="variant.product.unit_type.id !== 'piece'">
            за {{ variant.product.unit_type.short_name }}.
          </template>
        </template>
      </span>
    </router-link>
    <div class="mt-4 flex items-center justify-between sm:justify-start gap-4">
      <div v-if="!preview && !variant.is_promocode_reward" class="cart-product-quantity-controls-wrapper cart-modal-quantity-controls">
        <product-quantity-controls :product="variant.product"
                                   :selected-variant="variant.variant"
                                   :is-in-stock="true"
                                   :small="true"
                                   :modifiers="variantModifiers"
                                   :modifiers-hash="variant.modifiers_hash"
                                   :promotion-id="variant.promotion_id"
                                   :promotion-reward-id="variant.promotion_reward_id"
                                   :stock-id="variant.stock_id"
                                   :is-promocode-reward="variant.is_promocode_reward"
        />
      </div>
      <div v-else-if="preview" class="cart-product-quantity-text">
        {{ variant.quantity }} {{ variant.product.unit_type.short_name }}.
      </div>
      <div class="cart-product-total-price-wrapper">
        <template v-if="variant.is_reward">
          <strong class="text-primary-darker">
            <i class="fal fa-gift"></i>
            Подарок
          </strong>
        </template>
        <template v-else>
          <template v-if="variant.has_discount">
            <strong class="block line-through text-xs">{{ priceToText(variant.original_total_price) }}</strong>
            <strong>{{ priceToText(variant.total_price) }}</strong>
          </template>
          <strong v-else>{{ priceToText(variant.total_price) }}</strong>
        </template>
      </div>
      <div v-if="!preview && !variant.is_promocode_reward" class="cart-product-actions-wrapper">
        <a href="javascript:;" class="cart-product-remove-product-btn" @click="removeFromCart">
          <i class="fal" :class="{ 'fa-trash-alt': !variantIsRemoving, 'fa-spinner fa-spin': variantIsRemoving }"></i>
        </a>
      </div>
    </div>
  </div>
</div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import ProductQuantityControls from '@/components/products/partials/ProductQuantityControls'
import priceToText from '@/helpers/priceToText'
import ResponsiveImage from '@/components/images/ResponsiveImage'
import HasProductVariantDescriptions from '@/mixins/HasProductVariantDescriptions'
import zenkyEvents from '@/events'

export default {
  props: ['variant', 'preview'],

  mixins: [HasProductVariantDescriptions],

  components: {
    ProductQuantityControls,
    ResponsiveImage,
  },

  computed: {
    ...mapGetters({
      photoPlaceholder: 'ui/photoPlaceholder',
      promotionsAreChecking: 'order/promotionsAreChecking',
    }),

    shouldDisplayPreloader() {
      return !!this.variant.promotion_reward_id && this.promotionsAreChecking
    },

    hasMedia() {
      return Array.isArray(this.variant.product.media) && this.variant.product.media.length > 0
    },

    productRouteParams() {
      const params = {
        name: 'product',
        params: {
          productId: this.variant.product.short_id,
          productSlug: this.variant.product.slug,
          promotionId: this.variant.promotion_id,
          rewardId: this.variant.promotion_reward_id,
        },
        query: {
          variant: this.variant.variant.id,
        },
      }

      if (this.variant.promotion_id) {
        params.query.promotion_id = this.variant.promotion_id
      }

      if (this.variant.promotion_reward_id) {
        params.query.reward_id = this.variant.promotion_reward_id
      }

      return params
    },

    variantDescription() {
      return this.getProductVariantDescription(this.variant)
    },

    hasModifiers() {
      return this.hasProductVariantModifiers(this.variant)
    },

    variantModifiers() {
      return this.getProductVariantModifiers(this.variant)
    },

    variantModifiersList() {
      return this.getProductVariantModifiersList(this.variant)
    },
  },

  methods: {
    ...mapActions({
      addProductVariant: 'order/addProductVariant',
      removeProductVariant: 'order/removeProductVariant',
    }),

    changeVariantQuantity(type, quantity = 1) {
      return new Promise((resolve, reject) => {
        if (this.variantIsBusy) {
          return
        }

        this.variantIsBusy = true

        const method = type === 'increase' ? 'addProductVariant' : 'removeProductVariant'

        const payload = {
          variantId: this.variant.variant.id, quantity,
          modifiers: this.variantModifiers,
          promotion_id: this.variant.promotion_id,
          promotion_reward_id: this.variant.promotion_reward_id,
          stock_id: this.variant.stock_id,
        }

        this[method](payload)
          .then(() => {
            this.variantIsBusy = false
            resolve()
          })
          .catch(err => {
            this.variantIsBusy = false
            reject(err)
          })
      })
    },

    removeFromCart() {
      this.variantIsRemoving = true

      const product = { ...this.variant.product }
      const variant = { ...this.variant.variant }
      const quantity = this.variant.quantity

      this.changeVariantQuantity('decrease', this.variant.quantity)
        .then(() => {
          this.$root.sharedEventBus.$emit(zenkyEvents.PRODUCT_REMOVED_FROM_ORDER, {
            quantity,
            product,
            variant,
          })

          this.variantIsRemoving = false
        })
        .catch(() => this.variantIsRemoving = false)
    },
    priceToText,
  },

  data() {
    return {
      variantIsBusy: false,
      variantIsRemoving: false,
    }
  }
}
</script>
